"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createMilestoneAction,
  updateMilestoneAction,
  deleteMilestoneAction,
} from "@/actions/projects-actions";
import { Calendar, Trash2, Edit3, Plus, AlertCircle } from "lucide-react";

interface Milestone {
  id: string;
  projectId: string;
  title: string;
  dueDate: Date | string;
  status: "todo" | "done" | "delayed";
}

interface MilestoneListProps {
  projectId: string;
  milestones: Milestone[];
}

export function MilestoneList({ projectId, milestones }: MilestoneListProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<Milestone | null>(
    null,
  );
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState<"todo" | "done" | "delayed">("todo");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleOpenAdd = () => {
    setEditingMilestone(null);
    setTitle("");
    // Default to 30 days from now in YYYY-MM-DD format
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 30);
    setDueDate(defaultDate.toISOString().split("T")[0]);
    setStatus("todo");
    setError("");
    setIsModalOpen(true);
  };

  const handleOpenEdit = (milestone: Milestone) => {
    setEditingMilestone(milestone);
    setTitle(milestone.title);
    // Format Date to YYYY-MM-DD for input[type=date]
    const dateObj = new Date(milestone.dueDate);
    const formattedDate = dateObj.toISOString().split("T")[0];
    setDueDate(formattedDate);
    setStatus(milestone.status);
    setError("");
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Il titolo della milestone è obbligatorio.");
      return;
    }
    if (!dueDate) {
      setError("La data di scadenza è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      let res;
      if (editingMilestone) {
        res = await updateMilestoneAction({
          id: editingMilestone.id,
          projectId,
          title,
          dueDate,
          status,
        });
      } else {
        res = await createMilestoneAction({
          projectId,
          title,
          dueDate,
          status,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsModalOpen(false);
      }
    } catch (err: any) {
      setError(
        err.message || "Si è verificato un errore durante il salvataggio.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (milestoneId: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa milestone?")) return;

    try {
      const res = await deleteMilestoneAction(milestoneId, projectId);
      if (res?.error) {
        alert(res.error);
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  const formatDate = (dateValue: Date | string) => {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (msStatus: "todo" | "done" | "delayed") => {
    switch (msStatus) {
      case "done":
        return <Badge variant="success">Completata</Badge>;
      case "delayed":
        return <Badge variant="error">In Ritardo</Badge>;
      case "todo":
      default:
        return <Badge variant="neutral">Pianificata</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider">
          Milestones del Progetto
        </h3>
        <Button size="sm" className="gap-1.5" onClick={handleOpenAdd}>
          <Plus className="h-3.5 w-3.5" />
          Aggiungi Milestone
        </Button>
      </div>

      {milestones.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 border border-dashed border-border rounded-xl bg-card/30 text-center">
          <AlertCircle className="h-8 w-8 text-text-muted mb-2" />
          <p className="text-sm text-text-secondary font-medium">
            Nessuna milestone configurata
          </p>
          <p className="text-xs text-text-muted mt-1 max-w-xs">
            Le milestone ti aiutano a pianificare e tracciare le scadenze chiave
            di questo progetto.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="min-w-full divide-y divide-border text-left text-sm">
            <thead className="bg-card-muted text-text-secondary font-semibold">
              <tr>
                <th className="px-6 py-3">Milestone</th>
                <th className="px-6 py-3">Scadenza</th>
                <th className="px-6 py-3">Stato</th>
                <th className="px-6 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50 text-text-primary">
              {milestones.map((ms) => (
                <tr
                  key={ms.id}
                  className="hover:bg-card-hover/40 transition-colors"
                >
                  <td className="px-6 py-4 font-medium">{ms.title}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-text-secondary">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(ms.dueDate)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(ms.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-xs font-medium space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-text-secondary hover:text-text-primary"
                      onClick={() => handleOpenEdit(ms)}
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-error hover:text-error/80 hover:bg-error-soft/10"
                      onClick={() => handleDelete(ms.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMilestone
                ? "Modifica Milestone"
                : "Aggiungi Nuova Milestone"}
            </DialogTitle>
            <DialogDescription>
              {editingMilestone
                ? "Aggiorna le informazioni della milestone per questo progetto."
                : "Definisci un nuovo traguardo temporale per tracciare le consegne chiave."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="ms-title">Titolo Milestone</Label>
              <Input
                id="ms-title"
                placeholder="es. Rilascio Alpha in Staging"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ms-dueDate">Data di Scadenza</Label>
                <Input
                  id="ms-dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ms-status">Stato</Label>
                <Select
                  id="ms-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="todo">Pianificata (Todo)</option>
                  <option value="done">Completata (Done)</option>
                  <option value="delayed">In Ritardo (Delayed)</option>
                </Select>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingMilestone ? "Salva Modifiche" : "Crea Milestone"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
